import { useTranslation } from "react-i18next"

import type { PeopleFilters } from "./fair-use-people-view"
import type { AdminColumnFilterValue, AdminColumnOption } from "../admin-column-filter-types"
import type { FairUseAccessGroup } from "@/api/admin/fair-use"

import { TableHead, TableHeader, TableRow } from "@/components/ui/table"

import { AdminColumnFilter } from "../admin-column-filter"

export function FairUsePeopleHeaders({
  filters,
  onChange,
  companies,
  departments,
  groups,
}: {
  filters: PeopleFilters
  onChange: (next: PeopleFilters) => void
  companies: string[]
  departments: string[]
  groups: FairUseAccessGroup[]
}) {
  const { t } = useTranslation()
  const values = (items: string[]) =>
    items.map((value) => ({
      value,
      label: value || t("adminDashboard.columnFilters.blanks", "(Blanks)"),
    }))
  const columns: {
    field: "search" | "plan" | "company" | "department" | "group" | "lead" | "usage"
    key: string
    label: string
    options?: AdminColumnOption[]
    excludeFromAll?: boolean
  }[] = [
    { field: "search", key: "name", label: t("adminDashboard.fairUse.users.table.user", "User") },
    {
      field: "plan",
      key: "plan",
      label: t("adminDashboard.peopleDirectory.plan", "Plan"),
      options: ["standard", "pro", "unlimited", "custom"].map((value) => ({
        value,
        label: t(`adminDashboard.fairUse.plans.${value}`, value),
      })),
    },
    {
      field: "company",
      key: "company",
      label: t("adminDashboard.peopleDirectory.company", "Company"),
      options: values(companies),
      excludeFromAll: true,
    },
    {
      field: "department",
      key: "department",
      label: t("adminDashboard.peopleDirectory.department", "Department"),
      options: values(departments),
      excludeFromAll: true,
    },
    {
      field: "group",
      key: "group",
      label: t("adminDashboard.peopleDirectory.group", "Access group"),
      options: groups.map((group) => ({ value: group.id, label: group.name })),
    },
    {
      field: "lead",
      key: "lead",
      label: t("adminDashboard.peopleDirectory.lead", "Local AI Lead"),
      options: [
        { value: "lead", label: t("adminDashboard.peopleDirectory.isLead", "Is a lead") },
        {
          value: "assigned",
          label: t("adminDashboard.peopleDirectory.assigned", "Assigned to a lead"),
        },
        { value: "none", label: t("adminDashboard.columnFilters.noLead", "No lead") },
        { value: "unknown", label: t("adminDashboard.columnFilters.unknown", "Unknown") },
      ],
    },
    {
      field: "usage",
      key: "cost",
      label: `${t("adminDashboard.fairUse.users.table.spend", "Spend this month")} (SEK)`,
      options: [
        { value: "used", label: t("adminDashboard.peopleDirectory.used", "With usage") },
        { value: "unused", label: t("adminDashboard.peopleDirectory.unused", "No usage") },
        {
          value: "pending",
          label: t("adminDashboard.fairUse.ops.filters.pending", "Requests waiting"),
        },
        { value: "near", label: t("adminDashboard.fairUse.ops.filters.near", "Near their budget") },
        {
          value: "blocked",
          label: t("adminDashboard.fairUse.ops.filters.overUnenforced", "Over budget"),
        },
      ],
    },
  ]
  return (
    <TableHeader>
      <TableRow>
        {columns.map((column) => {
          let currentSort: "asc" | "desc" | undefined
          if (filters.sort.startsWith(`${column.key}-`))
            currentSort = filters.sort.endsWith("-desc") ? "desc" : "asc"
          let ariaSort: "ascending" | "descending" | "none" = "none"
          if (currentSort) ariaSort = currentSort === "asc" ? "ascending" : "descending"
          const value: AdminColumnFilterValue =
            column.field === "search"
              ? { search: filters.search }
              : { selected: filters[column.field] }
          if (column.field === "usage") {
            value.minimum = filters.minimumSpend
            value.maximum = filters.maximumSpend
          }
          if (column.field === "company") value.excluded = filters.companyExcluded ?? []
          if (column.field === "department") value.excluded = filters.departmentExcluded ?? []
          const exclusionActive =
            (column.field === "company" && (filters.companyExcluded?.length ?? 0) > 0) ||
            (column.field === "department" && (filters.departmentExcluded?.length ?? 0) > 0)
          const active =
            column.field === "search"
              ? Boolean(filters.search.trim())
              : filters[column.field] !== null ||
                exclusionActive ||
                (column.field === "usage" && Boolean(filters.minimumSpend || filters.maximumSpend))
          const apply = (next: AdminColumnFilterValue) => {
            if (column.field === "search") onChange({ ...filters, search: next.search ?? "" })
            else if (column.field === "usage")
              onChange({
                ...filters,
                usage: next.selected ?? null,
                minimumSpend: next.minimum ?? "",
                maximumSpend: next.maximum ?? "",
              })
            else if (column.field === "company")
              onChange({
                ...filters,
                company: next.selected ?? null,
                companyExcluded: next.selected == null ? [...(next.excluded ?? [])] : [],
              })
            else if (column.field === "department")
              onChange({
                ...filters,
                department: next.selected ?? null,
                departmentExcluded: next.selected == null ? [...(next.excluded ?? [])] : [],
              })
            else onChange({ ...filters, [column.field]: next.selected ?? null })
          }
          return (
            <TableHead
              key={column.key}
              aria-sort={ariaSort}
              className={column.field === "search" ? "min-w-[220px]" : "min-w-[150px]"}
            >
              <AdminColumnFilter
                active={active}
                excludeFromAll={column.excludeFromAll}
                label={column.label}
                numericRange={column.field === "usage"}
                options={column.options}
                sortDirection={currentSort}
                textSearch={column.field === "search"}
                value={value}
                onApply={apply}
                onClear={() => apply({ selected: null, search: "", minimum: "", maximum: "" })}
                onSort={(direction) => onChange({ ...filters, sort: `${column.key}-${direction}` })}
              />
            </TableHead>
          )
        })}
      </TableRow>
    </TableHeader>
  )
}
